import React, { useState, useEffect } from 'react';
import { Target, Plus, X, BookOpen, Brain, Lightbulb } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';
import { supabase } from '../../lib/supabase';

export interface LearningProfileStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

interface CurriculumTarget {
  id: string;
  title: string;
  description: string;
  subject: string;
  learning_objectives: string[];
}

export function LearningProfileStep({ wizardData, onStepComplete }: LearningProfileStepProps) {
  const [curriculumTargets, setCurriculumTargets] = useState<CurriculumTarget[]>([]);
  const [showAddTarget, setShowAddTarget] = useState(false);
  const [learningObjectives, setLearningObjectives] = useState<string[]>(['']);
  const [customInstructions, setCustomInstructions] = useState('');
  const [accommodations, setAccommodations] = useState<string[]>([]);

  const [newTarget, setNewTarget] = useState({
    title: '',
    description: '',
    subject: 'math' as 'math' | 'reading' | 'science' | 'general',
    learning_objectives: ['']
  });

  const currentChild = wizardData.createdChild || wizardData.selectedChild;

  const commonAccommodations = [
    'Extra time for activities',
    'Break tasks into smaller steps',
    'Visual supports and diagrams',
    'Repeated instructions',
    'Quiet workspace',
    'Movement breaks',
    'Simplified language',
    'Color-coded materials'
  ];

  // Initialize from wizardData
  useEffect(() => {
    if (wizardData.learningProfile) {
      setLearningObjectives(wizardData.learningProfile.learningObjectives.length > 0 
        ? wizardData.learningProfile.learningObjectives 
        : ['']);
      setCustomInstructions(wizardData.learningProfile.customInstructions || '');
      setAccommodations(wizardData.learningProfile.accommodations || []);
    }
  }, [wizardData.learningProfile]);

  // Fetch curriculum targets for existing child
  useEffect(() => {
    if (currentChild?.id) {
      fetchCurriculumTargets(currentChild.id);
    }
  }, [currentChild?.id]);

  // Update learning profile when state changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      const selectedTargetIds = curriculumTargets
        .filter(target => (wizardData.learningProfile?.curriculumTargets || []).includes(target.id))
        .map(target => target.id);

      onStepComplete({
        learningProfile: {
          curriculumTargets: selectedTargetIds,
          learningObjectives: learningObjectives.filter(obj => obj.trim()),
          customInstructions,
          accommodations
        }
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [learningObjectives, customInstructions, accommodations, curriculumTargets, wizardData.learningProfile, onStepComplete]);

  const fetchCurriculumTargets = async (childId: string) => {
    try {
      const { data, error } = await supabase
        .from('curriculum_targets')
        .select('*')
        .eq('child_id', childId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCurriculumTargets(data || []);
    } catch (error) {
      console.error('Error fetching curriculum targets:', error);
    }
  };


  const addObjective = () => {
    setLearningObjectives([...learningObjectives, '']);
  };

  const updateObjective = (index: number, value: string) => {
    const newObjectives = [...learningObjectives];
    newObjectives[index] = value;
    setLearningObjectives(newObjectives);
    // The debounced useEffect will handle updating the learning profile
  };

  const removeObjective = (index: number) => {
    if (learningObjectives.length > 1) {
      const newObjectives = learningObjectives.filter((_, i) => i !== index);
      setLearningObjectives(newObjectives);
      // The debounced useEffect will handle updating the learning profile
    }
  };

  const toggleCurriculumTarget = (targetId: string) => {
    const currentTargets = wizardData.learningProfile?.curriculumTargets || [];
    const newTargets = currentTargets.includes(targetId)
      ? currentTargets.filter(id => id !== targetId)
      : [...currentTargets, targetId];
    
    onStepComplete({
      learningProfile: {
        ...wizardData.learningProfile,
        curriculumTargets: newTargets
      }
    });
  };

  const toggleAccommodation = (accommodation: string) => {
    const newAccommodations = accommodations.includes(accommodation)
      ? accommodations.filter(a => a !== accommodation)
      : [...accommodations, accommodation];
    
    setAccommodations(newAccommodations);
    // The debounced useEffect will handle updating the learning profile
  };

  const handleAddCurriculumTarget = async () => {
    if (!currentChild || !newTarget.title.trim()) return;

    try {
      const { data, error } = await supabase
        .from('curriculum_targets')
        .insert({
          child_id: currentChild.id,
          title: newTarget.title,
          description: newTarget.description,
          subject: newTarget.subject,
          learning_objectives: newTarget.learning_objectives.filter(obj => obj.trim())
        })
        .select()
        .single();

      if (error) throw error;

      setCurriculumTargets(prev => [data, ...prev]);
      setShowAddTarget(false);
      setNewTarget({
        title: '',
        description: '',
        subject: 'math',
        learning_objectives: ['']
      });
    } catch (error) {
      console.error('Error adding curriculum target:', error);
      alert('Failed to save curriculum target. Please try again.');
    }
  };

  const addNewTargetObjective = () => {
    setNewTarget(prev => ({
      ...prev,
      learning_objectives: [...prev.learning_objectives, '']
    }));
  };

  const updateNewTargetObjective = (index: number, value: string) => {
    setNewTarget(prev => ({
      ...prev,
      learning_objectives: prev.learning_objectives.map((obj, i) => i === index ? value : obj)
    }));
  };

  const removeNewTargetObjective = (index: number) => {
    if (newTarget.learning_objectives.length > 1) {
      setNewTarget(prev => ({
        ...prev,
        learning_objectives: prev.learning_objectives.filter((_, i) => i !== index)
      }));
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Learning Profile Setup
        </h3>
        <p className="text-gray-600">
          Customize the learning experience for {currentChild?.name || 'your child'}
        </p>
      </div>

      {/* Curriculum Targets */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
              <Target className="w-5 h-5 mr-2 text-blue-600" />
              Curriculum Targets
            </h4>
            <p className="text-sm text-gray-600">
              Select specific learning goals to focus on (optional)
            </p>
          </div>
          <button
            onClick={() => setShowAddTarget(true)}
            className="flex items-center px-3 py-2 bg-[var(--accent-blue)] text-white rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Target
          </button>
        </div>

        {curriculumTargets.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {curriculumTargets.map((target) => (
              <div
                key={target.id}
                onClick={() => toggleCurriculumTarget(target.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  (wizardData.learningProfile?.curriculumTargets || []).includes(target.id)
                    ? 'border-[var(--accent-blue)] bg-[var(--accent-blue)] bg-opacity-10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{target.title}</h5>
                    <p className="text-sm text-gray-600 mt-1">{target.subject}</p>
                    {target.description && (
                      <p className="text-sm text-gray-600 mt-2">{target.description}</p>
                    )}
                  </div>
                  {(wizardData.learningProfile?.curriculumTargets || []).includes(target.id) && (
                    <div className="w-5 h-5 bg-[var(--accent-blue)] rounded-full flex items-center justify-center ml-2">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p>No curriculum targets added yet. Add one to create more targeted activities.</p>
          </div>
        )}
      </div>

      {/* Learning Objectives */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
          <Brain className="w-5 h-5 mr-2 text-purple-600" />
          Learning Objectives
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          What specific skills should the activities focus on? (optional)
        </p>
        
        <div className="space-y-3">
          {learningObjectives.map((objective, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={objective}
                onChange={(e) => updateObjective(index, e.target.value)}
                placeholder={`Learning objective ${index + 1}`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
              />
              {learningObjectives.length > 1 && (
                <button
                  onClick={() => removeObjective(index)}
                  className="p-2 text-[var(--accent-red)] hover:bg-[var(--accent-red)] hover:bg-opacity-10 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addObjective}
            className="text-purple-600 hover:text-purple-700 text-sm font-medium"
          >
            + Add another objective
          </button>
        </div>
      </div>

      {/* Custom Instructions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
          <Lightbulb className="w-5 h-5 mr-2 text-yellow-600" />
          Custom Instructions
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Any specific instructions or focus areas for the AI to consider
        </p>
        
        <textarea
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          placeholder="e.g., Focus on real-world applications, include visual examples, make it fun and engaging..."
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)] resize-none"
        />
      </div>

      {/* Accommodations */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Learning Accommodations</h4>
        <p className="text-sm text-gray-600 mb-4">
          Select any accommodations that help your child learn best
        </p>
        
        <div className="grid md:grid-cols-2 gap-3">
          {commonAccommodations.map((accommodation) => (
            <button
              key={accommodation}
              onClick={() => toggleAccommodation(accommodation)}
              className={`text-left p-3 rounded-lg border transition-colors text-sm font-medium ${
                accommodations.includes(accommodation)
                  ? 'border-green-500 bg-green-50 text-green-800'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:text-gray-900'
              }`}
            >
              <span className="block">{accommodation}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Add Curriculum Target Modal */}
      {showAddTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Curriculum Target</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={newTarget.title}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
                  placeholder="e.g., Year 2 Addition and Subtraction"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <select
                  value={newTarget.subject}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, subject: e.target.value as 'math' | 'reading' | 'science' | 'general' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
                >
                  <option value="math">Math</option>
                  <option value="reading">Reading</option>
                  <option value="science">Science</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTarget.description}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
                  rows={2}
                  placeholder="Brief description of the learning target"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Learning Objectives</label>
                {newTarget.learning_objectives.map((objective, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={objective}
                      onChange={(e) => updateNewTargetObjective(index, e.target.value)}
                      placeholder={`Objective ${index + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
                    />
                    {newTarget.learning_objectives.length > 1 && (
                      <button
                        onClick={() => removeNewTargetObjective(index)}
                        className="p-2 text-[var(--accent-red)] hover:bg-[var(--accent-red)] hover:bg-opacity-10 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addNewTargetObjective}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  + Add objective
                </button>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowAddTarget(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCurriculumTarget}
                  className="flex-1 px-4 py-2 bg-[var(--accent-blue)] text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Save Target
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}